import { FC, useState, useMemo, useEffect } from 'react';
import Button from '@/components/Button';
import Form from '@/components/Form';
import InputWrapper from '@/components/Form/InputWrapper';
import { gaPushEvent } from '@/utils/analytics';
import { ErrorException } from '@/models/error';
import { toast } from 'react-toastify';
import { AdminService } from '@/services/admin.service';
import { Organization } from '@/models/user';
import SSOGuidePanel from '../SSOGuidePanel';
import styles from './organization-form.module.scss';
import { IdPType } from '@/models/sso';

interface NewUserFormProps {
  organization?: Organization;
  onSubmit: () => void;
  onSSOToggle?: (enabled: boolean) => void;
  onGuideToggle?: (visible: boolean) => void;
}

const IDP_OPTIONS: { value: IdPType; label: string }[] = [
  { value: IdPType.OKTA, label: 'Okta' },
  { value: IdPType.AZURE_AD, label: 'Azure AD' },
  { value: IdPType.GOOGLE, label: 'Google' },
];

const OrganizationForm: FC<NewUserFormProps> = ({
  organization,
  onSubmit,
  onSSOToggle,
  onGuideToggle,
}) => {
  const type: 'new' | 'edit' =
    typeof organization === 'undefined' ? 'new' : 'edit';

  const [submitted, setSubmitted] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [seatLimit, setSeatLimit] = useState<number>(
    organization?.seat_limit ?? 20
  );

  // SSO-specific state (only for new organizations)
  const [enableSSO, setEnableSSO] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [zitadelOrgId, setZitadelOrgId] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [idpType, setIdpType] = useState<IdPType | ''>('');
  const [logoUrl, setLogoUrl] = useState('');
  const [cloudflareSubdomain, setCloudflareSubdomain] = useState('');

  // Notify parent when SSO is toggled
  useEffect(() => {
    onSSOToggle?.(enableSSO);
    if (enableSSO) {
      // Auto-open guide when SSO is enabled
      setShowGuide(true);
    } else {
      // Reset SSO fields when SSO is disabled to prevent stale validation errors
      setZitadelOrgId('');
      setSubdomain('');
      setIdpType('');
      setLogoUrl('');
      setCloudflareSubdomain('');
      setPartBChecklist({
        b1_create_organization: false,
        b2_add_org_owner: false,
        b3_configure_idp: false,
        b4_update_login_policy: false,
        b5_grant_project: false,
        b6_add_cloudflare_subdomain: false,
      });
    }
  }, [enableSSO, onSSOToggle]);

  // Notify parent when guide visibility changes
  useEffect(() => {
    onGuideToggle?.(enableSSO && showGuide);
  }, [enableSSO, showGuide, onGuideToggle]);

  // Part B checklist state (required before submission)
  const [partBChecklist, setPartBChecklist] = useState({
    b1_create_organization: false,
    b2_add_org_owner: false,
    b3_configure_idp: false,
    b4_update_login_policy: false,
    b5_grant_project: false,
    b6_add_cloudflare_subdomain: false,
  });

  const seatsUsed = organization?.seats_used ?? 0;
  const showWarning = useMemo(
    () => type === 'edit' && seatLimit < seatsUsed,
    [type, seatLimit, seatsUsed]
  );

  // Validation for SSO fields
  const isSSOValid = useMemo(() => {
    if (!enableSSO) return true;
    return (
      zitadelOrgId.trim() !== '' && subdomain.trim() !== '' && idpType !== ''
    );
  }, [enableSSO, zitadelOrgId, subdomain, idpType]);

  // Validation for Part B checklist
  const isPartBComplete = useMemo(() => {
    if (!enableSSO) return true;
    return Object.values(partBChecklist).every(Boolean);
  }, [enableSSO, partBChecklist]);

  // Auto-close guide when all checklist items are complete
  useEffect(() => {
    if (enableSSO && isPartBComplete) {
      setShowGuide(false);
    }
  }, [enableSSO, isPartBComplete]);

  const togglePartBItem = (key: keyof typeof partBChecklist) => {
    setPartBChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Subdomain validation (lowercase, alphanumeric, hyphens)
  const subdomainError = useMemo(() => {
    if (!subdomain) return null;
    const pattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
    if (!pattern.test(subdomain)) {
      return 'Use lowercase letters, numbers, and hyphens only';
    }
    return null;
  }, [subdomain]);

  // Check if Cloudflare subdomain matches the form subdomain
  const cloudflareSubdomainMatch = useMemo(() => {
    if (!cloudflareSubdomain || !subdomain) return false;
    return (
      cloudflareSubdomain.trim().toLowerCase() ===
      subdomain.trim().toLowerCase()
    );
  }, [cloudflareSubdomain, subdomain]);

  // Auto-check/uncheck the Cloudflare checklist item based on subdomain match
  useEffect(() => {
    setPartBChecklist((prev) => ({
      ...prev,
      b6_add_cloudflare_subdomain: cloudflareSubdomainMatch,
    }));
  }, [cloudflareSubdomainMatch]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    setFetching(true);
    try {
      const name = formData.get('fields[name]') as string;
      const seatLimitValue = formData.get('fields[seat_limit]');
      const parsedSeatLimit = seatLimitValue
        ? parseInt(seatLimitValue as string, 10)
        : undefined;

      let success: boolean;

      if (type === 'new') {
        if (enableSSO) {
          // Create SSO organization
          success = await AdminService.addOrganization({
            name,
            enable_sso: true,
            zitadel_org_id: zitadelOrgId.trim(),
            subdomain: subdomain.trim().toLowerCase(),
            idp_type: idpType as IdPType,
            logo_url: logoUrl.trim() || undefined,
            seat_limit: parsedSeatLimit,
          });
        } else {
          // Create legacy organization
          success = await AdminService.addOrganization({ name });
        }
      } else {
        // Edit existing organization
        success = await AdminService.updateOrganization(
          organization?.id as string,
          {
            name,
            seat_limit: parsedSeatLimit,
          }
        );
      }

      if (!success) {
        throw new Error(
          type === 'new'
            ? 'Organization creation failed'
            : 'Organization update failed'
        );
      }
      gaPushEvent('add_organization');
      setSubmitted(true);
      onSubmit();
    } catch (err) {
      const error = err as ErrorException;
      toast(error.message, { type: 'error' });
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className={styles['form-container']} data-sso-enabled={enableSSO}>
      <section className={styles['form-panel']}>
        <Form onSubmit={handleSubmit}>
          <InputWrapper>
            <input
              type="text"
              id="name"
              name="fields[name]"
              defaultValue={organization?.name}
              required
            />
            <label htmlFor="name">Name:</label>
          </InputWrapper>

          {type === 'new' && (
            <>
              {/* SSO Toggle */}
              <div className={styles['sso-toggle']}>
                <label className={styles['toggle-label']}>
                  <input
                    type="checkbox"
                    checked={enableSSO}
                    onChange={(e) => setEnableSSO(e.target.checked)}
                  />
                  <span className={styles['toggle-slider']}></span>
                  <span className={styles['toggle-text']}>
                    Enable SSO (Enterprise)
                  </span>
                </label>
              </div>

              {/* SSO Configuration Section */}
              {enableSSO && (
                <div className={styles['sso-config']}>
                  <div className={styles['warning-box']}>
                    <span className={styles['warning-icon']}>!</span>
                    <p>
                      Complete the Zitadel and Cloudflare setup steps before
                      submitting.
                      {showGuide
                        ? ' See the reference guide on the right.'
                        : ' Use the "Show Setup Guide" button below for detailed instructions.'}
                    </p>
                  </div>

                  {/* Setup Checklist */}
                  <div className={styles['checklist-section']}>
                    <div className={styles['checklist-header']}>
                      <h4>Setup Checklist</h4>
                      <span className={styles['checklist-count']}>
                        {Object.values(partBChecklist).filter(Boolean).length} /
                        6 complete
                      </span>
                    </div>
                    <div className={styles['checklist-items']}>
                      <span className={styles['checklist-group-label']}>
                        Zitadel
                      </span>
                      <label className={styles['checklist-item']}>
                        <input
                          type="checkbox"
                          checked={partBChecklist.b1_create_organization}
                          onChange={() =>
                            togglePartBItem('b1_create_organization')
                          }
                        />
                        <span>Create Organization in Zitadel</span>
                      </label>
                      <label className={styles['checklist-item']}>
                        <input
                          type="checkbox"
                          checked={partBChecklist.b2_add_org_owner}
                          onChange={() => togglePartBItem('b2_add_org_owner')}
                        />
                        <span>Add yourself as ORG_OWNER</span>
                      </label>
                      <label className={styles['checklist-item']}>
                        <input
                          type="checkbox"
                          checked={partBChecklist.b3_configure_idp}
                          onChange={() => togglePartBItem('b3_configure_idp')}
                        />
                        <span>Configure Identity Provider</span>
                      </label>
                      <label className={styles['checklist-item']}>
                        <input
                          type="checkbox"
                          checked={partBChecklist.b4_update_login_policy}
                          onChange={() =>
                            togglePartBItem('b4_update_login_policy')
                          }
                        />
                        <span>Update Login Policy</span>
                      </label>
                      <label className={styles['checklist-item']}>
                        <input
                          type="checkbox"
                          checked={partBChecklist.b5_grant_project}
                          onChange={() => togglePartBItem('b5_grant_project')}
                        />
                        <span>Grant Project to Organization</span>
                      </label>
                      <span className={styles['checklist-group-label']}>
                        Cloudflare
                      </span>
                      <div className={styles['checklist-item-with-input']}>
                        <div className={styles['checklist-item-header']}>
                          <input
                            type="checkbox"
                            checked={partBChecklist.b6_add_cloudflare_subdomain}
                            readOnly
                            disabled
                          />
                          <span>Add Subdomain in Cloudflare Pages</span>
                        </div>
                        <div className={styles['checklist-input-row']}>
                          <input
                            type="text"
                            className={styles['checklist-input']}
                            value={cloudflareSubdomain}
                            onChange={(e) =>
                              setCloudflareSubdomain(
                                e.target.value.toLowerCase()
                              )
                            }
                            placeholder="Enter subdomain configured in Cloudflare"
                          />
                          {cloudflareSubdomain && (
                            <span
                              className={
                                cloudflareSubdomainMatch
                                  ? styles['match-success']
                                  : styles['match-error']
                              }
                            >
                              {cloudflareSubdomainMatch
                                ? '✓ Match'
                                : '✗ Mismatch'}
                            </span>
                          )}
                        </div>
                        {cloudflareSubdomain && !cloudflareSubdomainMatch && (
                          <span className={styles['checklist-input-error']}>
                            Must match the subdomain below: "
                            {subdomain || '(not set)'}"
                          </span>
                        )}
                      </div>
                    </div>
                    {!isPartBComplete && (
                      <p className={styles['checklist-warning']}>
                        Complete all setup steps to enable form submission
                      </p>
                    )}
                    <button
                      type="button"
                      className={styles['guide-toggle-btn']}
                      onClick={() => setShowGuide((prev) => !prev)}
                    >
                      {showGuide ? 'Hide' : 'Show'} Setup Guide
                    </button>
                  </div>

                  <div className={styles['form-field']}>
                    <label
                      htmlFor="zitadel_org_id"
                      className={styles['field-label']}
                    >
                      Zitadel Organization ID{' '}
                      <span className={styles['required']}>*</span>
                    </label>
                    <input
                      type="text"
                      id="zitadel_org_id"
                      className={styles['field-input']}
                      value={zitadelOrgId}
                      onChange={(e) => setZitadelOrgId(e.target.value)}
                      required={enableSSO}
                      placeholder="e.g., 287431927498234"
                    />
                    <span className={styles['field-hint']}>
                      Found in Zitadel Console → Organization → Settings
                    </span>
                  </div>

                  <div className={styles['form-field']}>
                    <label className={styles['field-label']}>
                      Identity Provider{' '}
                      <span className={styles['required']}>*</span>
                    </label>
                    <div className={styles['idp-options']}>
                      {IDP_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          className={styles['idp-option']}
                        >
                          <input
                            type="radio"
                            name="idp_type"
                            value={option.value}
                            checked={idpType === option.value}
                            onChange={() => setIdpType(option.value)}
                            required={enableSSO}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className={styles['form-field']}>
                    <label
                      htmlFor="subdomain"
                      className={styles['field-label']}
                    >
                      Subdomain (slug){' '}
                      <span className={styles['required']}>*</span>
                    </label>
                    <div className={styles['subdomain-input']}>
                      <input
                        type="text"
                        id="subdomain"
                        className={styles['field-input']}
                        value={subdomain}
                        onChange={(e) =>
                          setSubdomain(e.target.value.toLowerCase())
                        }
                        required={enableSSO}
                        placeholder="xyz-org"
                        pattern="^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$"
                      />
                      <span className={styles['subdomain-suffix']}>
                        .workspace.gannet.ai
                      </span>
                    </div>
                    {subdomainError && (
                      <span className={styles['field-error']}>
                        {subdomainError}
                      </span>
                    )}
                    <span className={styles['field-hint']}>
                      Must match the subdomain configured in Cloudflare Pages
                    </span>
                  </div>

                  <div className={styles['form-field']}>
                    <label htmlFor="logo_url" className={styles['field-label']}>
                      Logo URL{' '}
                      <span className={styles['optional']}>(optional)</span>
                    </label>
                    <input
                      type="url"
                      id="logo_url"
                      className={styles['field-input']}
                      value={logoUrl}
                      onChange={(e) => setLogoUrl(e.target.value)}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>

                  <div className={styles['form-field']}>
                    <label
                      htmlFor="seat_limit_new"
                      className={styles['field-label']}
                    >
                      Seat Limit
                    </label>
                    <input
                      type="number"
                      id="seat_limit_new"
                      className={styles['field-input']}
                      name="fields[seat_limit]"
                      value={seatLimit}
                      onChange={(e) =>
                        setSeatLimit(parseInt(e.target.value, 10) || 1)
                      }
                      min={1}
                      max={10000}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {type === 'edit' && (
            <InputWrapper>
              <input
                type="number"
                id="seat_limit"
                name="fields[seat_limit]"
                value={seatLimit}
                onChange={(e) =>
                  setSeatLimit(parseInt(e.target.value, 10) || 1)
                }
                min={1}
                max={10000}
                required
              />
              <label htmlFor="seat_limit">
                Seat Limit ({seatsUsed} currently used):
              </label>
              {showWarning && (
                <p className={styles['seat-warning']}>
                  Warning: {seatsUsed - seatLimit} user
                  {seatsUsed - seatLimit !== 1 ? 's' : ''} will be blocked from
                  access
                </p>
              )}
            </InputWrapper>
          )}

          <Button
            variation="alt"
            disabled={
              fetching ||
              submitted ||
              (enableSSO && !isSSOValid) ||
              (enableSSO && !isPartBComplete) ||
              !!subdomainError
            }
            loading={fetching}
            loadingText="Saving"
          >
            {type === 'new' ? 'Add' : 'Update'} Organization
          </Button>
        </Form>
      </section>

      {/* Guide Panel - visible when SSO is enabled and showGuide is true */}
      {enableSSO && showGuide && (
        <div className={styles['guide-panel']}>
          <SSOGuidePanel />
        </div>
      )}
    </div>
  );
};

export default OrganizationForm;
